/**
 * One-shot web-client self-diagnostic.
 *
 * The browser voice path fails in different places than the desktop client, so
 * the checks differ: there's no raw UDP to probe (media rides WebCodecs + a
 * WebSocket the browser tunnels over TCP/TLS), but there ARE browser-only traps
 * the desktop never hits, chiefly the iOS Safari WebCodecs gate (Lockdown Mode,
 * the WebCodecs Feature Flag, in-app WebViews) and microphone permission.
 *
 * This runner gathers every check into a structured report and a flat text log
 * the user can download and send to us. Each line leads with the literal
 * observed value (which constructor is missing, which getUserMedia error name,
 * the roster count) so the log is self-diagnosing without a back-and-forth.
 */
import { fetchHealth, refreshSession } from '../api.ts'
import { getRefreshToken, getRegion, getServerId } from '../auth.ts'
import { probeAudioSupport, type AudioDiagnostics } from './audio.ts'

export type DiagStatus = 'ok' | 'warn' | 'fail' | 'info'

export interface DiagCheck {
  readonly status: DiagStatus
  readonly title: string
  readonly detail: string
}

/** Live pipeline facts the page already holds; folded into the report so the
 * log captures the running session, not just a cold probe. */
export interface DiagLiveState {
  readonly connected: boolean
  readonly selfAgid: string | null
  readonly rosterCount: number
  readonly audibleCount: number
  /** Whether our own AGID appears in the bridge roster. A populated roster that
   * excludes us means wrong server / not in-game, not a healthy state. */
  readonly selfInRoster: boolean
}

export interface DiagReport {
  readonly checks: ReadonlyArray<DiagCheck>
  readonly verdict: string
  /** Full plain-text log, ready to write to a .log file. */
  readonly logText: string
}

const WS_PROBE_TIMEOUT_MS = 6000

export async function runWebDiagnostics(live: DiagLiveState): Promise<DiagReport> {
  const checks: DiagCheck[] = []
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '(no navigator)'
  const region = getRegion()
  const serverId = getServerId()

  checks.push({
    status: 'info',
    title: 'Environment',
    detail: `region=${region}, server=${serverId ?? '(none selected)'}, origin=${location.origin}`,
  })

  // Secure context gates getUserMedia + WebCodecs + AudioWorklet. If this is
  // false nothing below can work, so call it out first.
  const secure = window.isSecureContext === true
  checks.push({
    status: secure ? 'ok' : 'fail',
    title: 'Secure context (HTTPS)',
    detail: secure
      ? 'Page is a secure context; mic + audio APIs are permitted.'
      : 'Not a secure context. Microphone and audio APIs are blocked off HTTPS.',
  })

  // WebCodecs / Opus support. The single biggest reason the web client fails on
  // iOS. Reuse the canonical probe so the verdict matches the fatal screen.
  const audio = await probeAudioSupport()
  checks.push(audioCheck(audio.diagnostics))

  // AudioWorklet is required for the mic-capture + playback-ring worklets.
  const workletOk = typeof AudioWorkletNode === 'function' &&
    typeof (globalThis as { AudioContext?: unknown }).AudioContext === 'function'
  checks.push({
    status: workletOk ? 'ok' : 'fail',
    title: 'AudioContext / AudioWorklet',
    detail: workletOk
      ? 'AudioContext and AudioWorklet are available.'
      : 'AudioContext or AudioWorklet is missing; the capture/playback pipeline cannot start.',
  })

  // Microphone: actually try to acquire it so permission / no-device states are
  // observed rather than assumed.
  checks.push(await micCheck(secure))

  // HTTP API.
  let healthOk = false
  try {
    const h = await fetchHealth()
    healthOk = true
    checks.push({
      status: 'ok',
      title: 'HTTP API',
      detail: `Reachable (region ${h.region}, uptime ${h.uptimeSeconds}s).`,
    })
  } catch (err) {
    checks.push({ status: 'fail', title: 'HTTP API', detail: `Unreachable: ${msg(err)}` })
  }

  // Session refresh (needs a stored token).
  const refreshToken = getRefreshToken()
  let sessionToken: string | null = null
  if (refreshToken === null) {
    checks.push({ status: 'fail', title: 'Account linked', detail: 'No refresh token stored. Link your account first.' })
  } else if (!healthOk) {
    checks.push({ status: 'warn', title: 'Session token', detail: 'Skipped: HTTP API was unreachable.' })
  } else {
    try {
      const r = await refreshSession(refreshToken)
      sessionToken = r.sessionToken
      checks.push({ status: 'ok', title: 'Session token', detail: `Refresh accepted (expires ${r.expiresAt}).` })
    } catch (err) {
      checks.push({ status: 'fail', title: 'Session token', detail: `Refresh rejected: ${msg(err)}. Re-link to fix.` })
    }
  }

  // Voice WebSocket reachability.
  let wsOk = false
  if (sessionToken === null) {
    checks.push({ status: 'warn', title: 'Voice WebSocket', detail: 'Skipped: no session token.' })
  } else if (serverId === null) {
    checks.push({ status: 'warn', title: 'Voice WebSocket', detail: 'Skipped: no server selected.' })
  } else {
    try {
      await probeVoiceWs(sessionToken, serverId)
      wsOk = true
      checks.push({ status: 'ok', title: 'Voice WebSocket', detail: 'Connected and closed cleanly.' })
    } catch (err) {
      checks.push({ status: 'fail', title: 'Voice WebSocket', detail: `Failed: ${msg(err)}` })
    }
  }

  // Live session state (what the page is currently seeing).
  checks.push({
    status: live.connected ? 'ok' : 'warn',
    title: 'Live connection',
    detail: live.connected
      ? `Connected${live.selfAgid ? ` as ${live.selfAgid}` : ''}.`
      : 'Not currently connected to voice on this page.',
  })
  checks.push({
    status: live.rosterCount > 0 && live.selfInRoster ? 'ok' : 'warn',
    title: 'Players online (roster)',
    detail: live.rosterCount === 0
      ? 'Empty. Either you are not in-game on the selected server, or no snapshot has arrived yet.'
      : live.selfInRoster
        ? `${live.rosterCount} player(s) on the server (${live.audibleCount} in audible range), including you.`
        : `${live.rosterCount} player(s) on the server, but you are not in the list. The selected server likely doesn't match the one you're playing on.`,
  })

  const verdict = buildVerdict({ secure, audio: audio.diagnostics, healthOk, hasToken: refreshToken !== null, sessionToken, wsOk, live, ua })
  return { checks, verdict, logText: buildLog(checks, verdict, ua, audio.diagnostics) }
}

function audioCheck(diag: AudioDiagnostics): DiagCheck {
  if (diag.outcome === 'ok') {
    return { status: 'ok', title: 'WebCodecs (Opus)', detail: 'AudioEncoder + AudioDecoder present and an Opus config negotiated.' }
  }
  const lockdown = diag.webAssembly === 'missing'
  const base =
    diag.outcome === 'no-encoder-api' ? 'AudioEncoder constructor is missing (needed to send your mic).'
      : diag.outcome === 'no-decoder-api' ? 'AudioDecoder constructor is missing (needed to hear others).'
        : diag.outcome === 'no-encoder-config' ? 'AudioEncoder is present but no Opus encoder config was accepted.'
          : 'AudioDecoder is present but the Opus decoder config was not accepted.'
  const hint = lockdown
    ? ' WebAssembly is also missing, which on iOS Safari almost always means Lockdown Mode is on.'
    : ' On iOS Safari, check Settings -> Safari -> Advanced -> Feature Flags -> WebCodecs is ON.'
  return { status: 'fail', title: 'WebCodecs (Opus)', detail: base + hint }
}

async function micCheck(secure: boolean): Promise<DiagCheck> {
  if (!secure) return { status: 'warn', title: 'Microphone', detail: 'Skipped: not a secure context.' }
  const md = navigator.mediaDevices
  if (md === undefined || typeof md.getUserMedia !== 'function') {
    return { status: 'fail', title: 'Microphone', detail: 'getUserMedia is unavailable in this browser.' }
  }
  try {
    const stream = await md.getUserMedia({ audio: true })
    const track = stream.getAudioTracks()[0]
    const label = track !== undefined && track.label !== '' ? track.label : '(device name withheld until granted)'
    for (const t of stream.getTracks()) t.stop()
    return { status: 'ok', title: 'Microphone', detail: `Permission granted; capturing from "${label}".` }
  } catch (err) {
    const name = err instanceof DOMException ? err.name : 'Error'
    const why = name === 'NotAllowedError'
      ? 'Permission denied. Allow microphone access for this site and reload.'
      : name === 'NotFoundError'
        ? 'No microphone found. Plug one in or select a different input device.'
        : msg(err)
    return { status: 'fail', title: 'Microphone', detail: `${name}: ${why}` }
  }
}

/** Transiently open the voice WS as a probe, then close. Mirrors the bridge's
 * voice-ws auth path. Resolves on open, rejects on close-before-open / timeout. */
function probeVoiceWs(sessionToken: string, serverId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const wsBase = location.origin.replace(/^http/, 'ws')
    const url = `${wsBase}/api/voice-ws?token=${encodeURIComponent(sessionToken)}&serverId=${encodeURIComponent(serverId)}`
    const ws = new WebSocket(url)
    ws.binaryType = 'arraybuffer'
    const timeout = setTimeout(() => {
      try { ws.close() } catch { /* ignore */ }
      reject(new Error(`timed out after ${WS_PROBE_TIMEOUT_MS}ms waiting for open`))
    }, WS_PROBE_TIMEOUT_MS)
    ws.addEventListener('open', () => {
      clearTimeout(timeout)
      try { ws.close(1000, 'diagnostic ok') } catch { /* ignore */ }
      resolve()
    })
    ws.addEventListener('close', (e) => {
      clearTimeout(timeout)
      if (e.code === 1000) return
      reject(new Error(`closed before open (code=${e.code} reason=${e.reason || 'none'})`))
    })
  })
}

interface VerdictInputs {
  readonly secure: boolean
  readonly audio: AudioDiagnostics
  readonly healthOk: boolean
  readonly hasToken: boolean
  readonly sessionToken: string | null
  readonly wsOk: boolean
  readonly live: DiagLiveState
  readonly ua: string
}

/** Priority-ordered single root cause, most-actionable first. */
function buildVerdict(v: VerdictInputs): string {
  if (!v.secure) {
    return 'This page is not running over HTTPS, so the browser blocks the microphone and audio APIs. Open the official https:// link.'
  }
  if (v.audio.outcome !== 'ok') {
    const isIos = /iPhone|iPad|iPod/.test(v.ua) || (v.ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
    if (v.audio.webAssembly === 'missing') {
      return 'WebCodecs is disabled (WebAssembly is missing too, the Lockdown Mode give-away). Turn off Lockdown Mode for this site, or use the desktop client.'
    }
    if (isIos) {
      return 'Your browser is missing the WebCodecs audio API the voice client needs. On iPhone/iPad: open in real Safari, update iOS, and check Settings -> Safari -> Advanced -> Feature Flags -> WebCodecs is ON. The desktop client is the reliable alternative.'
    }
    return 'Your browser is missing or rejecting the WebCodecs Opus API. Update to a recent Safari 18.4+ / Chrome / Edge / Firefox, or use the desktop client.'
  }
  if (!v.healthOk) {
    return 'The bridge HTTP API is unreachable. Check your internet connection and that you opened the correct region link.'
  }
  if (!v.hasToken) {
    return 'You are not linked. Use the Link flow to claim a code in-game first.'
  }
  if (v.sessionToken === null) {
    return 'Your saved login was rejected by the bridge. Re-link your account to mint a fresh token.'
  }
  if (!v.wsOk) {
    return 'HTTP works but the voice WebSocket would not connect. A proxy, content blocker, or firewall is likely blocking WebSocket traffic on this network.'
  }
  if (v.live.rosterCount === 0) {
    return 'Everything connects, but the bridge reports no players on the selected server. Make sure the game is running and the selected server matches the one you are on.'
  }
  if (!v.live.selfInRoster) {
    return 'You connect fine but you are not in the server\'s player list. The server selected here does not match the one you are playing on. Pick the correct server.'
  }
  return 'All checks passed: HTTPS, WebCodecs, microphone, HTTP, session, and the voice WebSocket are healthy. If you still cannot hear someone, check per-player mute/volume.'
}

function buildLog(checks: ReadonlyArray<DiagCheck>, verdict: string, ua: string, diag: AudioDiagnostics): string {
  const glyph = (s: DiagStatus): string => s === 'ok' ? '[ OK ]' : s === 'warn' ? '[ !! ]' : s === 'fail' ? '[ XX ]' : '[ -- ]'
  const lines: string[] = []
  lines.push('AshenAge Voice - web client diagnostics')
  lines.push(`generated: ${new Date().toISOString()}`)
  lines.push(`userAgent: ${ua}`)
  lines.push('='.repeat(60))
  for (const c of checks) {
    lines.push(`${glyph(c.status)} ${c.title}`)
    lines.push(`        ${c.detail}`)
  }
  lines.push('='.repeat(60))
  lines.push(`VERDICT: ${verdict}`)
  lines.push('')
  // Raw WebCodecs probe table for completeness (the per-config attempts).
  lines.push('--- WebCodecs probe detail ---')
  lines.push(`AudioEncoder: ${diag.audioEncoder}  AudioDecoder: ${diag.audioDecoder}  AudioContext: ${diag.audioContext}  WebAssembly: ${diag.webAssembly}`)
  lines.push(`outcome: ${diag.outcome}`)
  for (const p of diag.encoderProbes) lines.push(`  encoder ${p.label}: ${p.status}${p.error ? ` (${p.error})` : ''}`)
  for (const p of diag.decoderProbes) lines.push(`  decoder ${p.label}: ${p.status}${p.error ? ` (${p.error})` : ''}`)
  return lines.join('\n')
}

function msg(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}
